import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Favorites (wishlist) panel of the cart drawer.
 *
 * Reads the customer wishlist from the Advanced Wishlist (Wishlist Power) app
 * (`window.Maestrooo.wishlist`), keeps the "Favoris (N)" tab counter in sync,
 * and renders each favorite as a product card (image, title, price, options,
 * add-to-cart, remove).
 *
 * The wishlist payload only carries title / handle / variant_id / variant_title
 * / featured_media_url — NOT price or option names — so each item's product JSON
 * is fetched once from `/products/{handle}.js` (cached) to render price +
 * "Option : value" lines faithfully.
 *
 * Re-renders on:
 *   - connect / init (initial load),
 *   - `ooo-wishlist:product-added` / `-removed` (wishlist changed),
 *   - `cart:update` (a cart morph wipes the JS-rendered cards — re-render after).
 *
 * ⚠️ Requires the Advanced Wishlist app embed (setup) enabled. Without it the
 *    API never appears and the panel stays on its empty message.
 *
 * Pack products (component breakdown disclosure) are rendered as plain cards for
 * now — the component list needs pack metadata (follow-up).
 *
 * @typedef {object} WishlistItem
 * @property {string} handle
 * @property {number|null} [variant_id]
 * @property {string} [title]
 * @property {string} [variant_title]
 * @property {string} [featured_media_url]
 */
class CartDrawerFavorites extends HTMLElement {
  /** @type {Map<string, object|null>} handle → product JSON (null = fetch failed) */
  #productCache = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} Guards against out-of-order renders. */
  #renderToken = 0;

  connectedCallback() {
    document.addEventListener('ooo-wishlist:product-added', this.#handleWishlistChange);
    document.addEventListener('ooo-wishlist:product-removed', this.#handleWishlistChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#init();
  }

  disconnectedCallback() {
    document.removeEventListener('ooo-wishlist:product-added', this.#handleWishlistChange);
    document.removeEventListener('ooo-wishlist:product-removed', this.#handleWishlistChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#abortController?.abort();
  }

  // ─── Config (data attributes) ─────────────────────────────

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'EUR';
  }

  // ─── Init / events ────────────────────────────────────────

  async #init() {
    const wishlist = await this.#waitForWishlist();
    if (!wishlist) return;

    try {
      const content = await wishlist.getContent();
      this.#update(content?.products ?? []);
    } catch {
      // Wishlist API not ready / network — leave the empty state in place.
    }
  }

  /**
   * @param {Event & { detail?: { wishlist_product_list?: WishlistItem[], wishlist_products_count?: number } }} event
   */
  #handleWishlistChange = (event) => {
    const list = event.detail?.wishlist_product_list;
    if (Array.isArray(list)) {
      this.#update(list);
      return;
    }
    this.#init();
  };

  // A cart morph (add-to-cart, qty change…) replaces this panel's server-rendered
  // children, wiping the JS-rendered cards. Re-render on the next frame, once the
  // morph has settled.
  #handleCartUpdate = () => {
    requestAnimationFrame(() => this.#init());
  };

  /**
   * Polls for `window.Maestrooo.wishlist` (the app loads asynchronously).
   * @param {number} [timeout]
   * @returns {Promise<{ getContent: () => Promise<{products: WishlistItem[]}>, removeProduct: (handle: string, variantId?: number|null) => Promise<unknown> } | null>}
   */
  #waitForWishlist(timeout = 8000) {
    return new Promise((resolve) => {
      const start = performance.now();
      const check = () => {
        const wishlist = window.Maestrooo?.wishlist;
        if (wishlist?.getContent) {
          resolve(wishlist);
          return;
        }
        if (performance.now() - start > timeout) {
          resolve(null);
          return;
        }
        setTimeout(check, 200);
      };
      check();
    });
  }

  // ─── Render ───────────────────────────────────────────────

  /** @param {WishlistItem[]} products */
  #update(products) {
    this.#updateCount(products.length);
    this.#render(products);
  }

  /** @param {number} count */
  #updateCount(count) {
    for (const el of document.querySelectorAll('[data-favorites-count]')) {
      el.textContent = String(count);
    }
  }

  /** @param {WishlistItem[]} products */
  async #render(products) {
    const list = this.querySelector('[data-favorites-list]');
    const empty = this.querySelector('[data-favorites-empty]');
    if (!(list instanceof HTMLElement)) return;

    const token = ++this.#renderToken;

    if (!products.length) {
      list.replaceChildren();
      empty?.removeAttribute('hidden');
      return;
    }

    empty?.setAttribute('hidden', '');

    const cards = await Promise.all(products.map((item) => this.#buildCard(item)));

    // A newer render started while we were fetching — drop this stale result.
    if (token !== this.#renderToken) return;

    list.replaceChildren(...cards.filter((card) => card instanceof HTMLElement));
  }

  /**
   * @param {WishlistItem} item
   * @returns {Promise<HTMLElement|null>}
   */
  async #buildCard(item) {
    const handle = item.handle;
    if (!handle) return null;

    const product = await this.#fetchProduct(handle);
    const variant = this.#resolveVariant(product, item.variant_id);

    const root = `${window.Shopify?.routes?.root ?? '/'}`.replace(/\/$/, '');
    const url = `${root}/products/${handle}`;
    const title = item.title || product?.title || '';
    const image = item.featured_media_url || variant?.featured_image?.src || product?.featured_image || '';

    const li = document.createElement('li');
    li.className = 'cart-drawer__favorite';
    li.dataset.handle = handle;
    if (item.variant_id) li.dataset.variantId = String(item.variant_id);

    li.innerHTML = `
      <a class="cart-drawer__favorite-media" href="${escapeAttr(url)}" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${escapeAttr(sizedImage(image, 170))}" alt="" width="85" height="85" loading="lazy" decoding="async">` : ''}
      </a>
      <div class="cart-drawer__favorite-info">
        <div class="cart-drawer__favorite-head">
          <a class="cart-drawer__favorite-title" href="${escapeAttr(url)}">${escapeHtml(title)}</a>
          ${this.#priceMarkup(variant)}
        </div>
        ${this.#optionsMarkup(product, variant, item)}
        <div class="cart-drawer__favorite-actions">
          ${
            variant && variant.available
              ? `<button type="button" class="cart-drawer__favorite-add button-unstyled link-underlined" data-action="add">
                  <span class="cart-drawer__favorite-add-label">${escapeHtml(Theme?.translations?.favorites_add_to_cart ?? 'Ajouter au panier')}</span>
                </button>`
              : `<span class="cart-drawer__favorite-unavailable">${escapeHtml(Theme?.translations?.favorites_unavailable ?? 'Indisponible')}</span>`
          }
          <button type="button" class="cart-drawer__favorite-remove button-unstyled" data-action="remove" aria-label="${escapeAttr((Theme?.translations?.favorites_remove ?? 'Retirer des favoris'))}">
            <span class="svg-wrapper" aria-hidden="true">${CLOSE_SVG}</span>
          </button>
        </div>
      </div>
    `;

    const addButton = li.querySelector('[data-action="add"]');
    addButton?.addEventListener('click', () => this.#addToCart(variant, li));

    const removeButton = li.querySelector('[data-action="remove"]');
    removeButton?.addEventListener('click', () => this.#removeFavorite(item, li));

    return li;
  }

  /**
   * @param {object|null} variant
   * @returns {string}
   */
  #priceMarkup(variant) {
    if (!variant) return '';
    const price = this.#money(variant.price);
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (onSale) {
      return `<span class="cart-drawer__favorite-prices">
        <span class="cart-drawer__favorite-price cart-drawer__favorite-price--sale">${price}</span>
        <s class="cart-drawer__favorite-compare">${this.#money(variant.compare_at_price)}</s>
      </span>`;
    }

    return `<span class="cart-drawer__favorite-prices"><span class="cart-drawer__favorite-price">${price}</span></span>`;
  }

  /**
   * Builds the "Option : value" lines from product options + the resolved variant.
   * Falls back to the wishlist `variant_title` when the product fetch failed.
   * @param {object|null} product
   * @param {object|null} variant
   * @param {WishlistItem} item
   * @returns {string}
   */
  #optionsMarkup(product, variant, item) {
    /** @type {string[]} */
    const lines = [];

    if (product && variant && Array.isArray(product.options)) {
      product.options.forEach((option, index) => {
        const name = typeof option === 'string' ? option : option?.name;
        const value = Array.isArray(variant.options) ? variant.options[index] : variant[`option${index + 1}`];
        if (!name || !value || value === 'Default Title' || name.toLowerCase() === 'title') return;
        lines.push(`<span class="cart-drawer__favorite-option">${escapeHtml(name)} : ${escapeHtml(value)}</span>`);
      });
    }

    if (!lines.length && item.variant_title && item.variant_title !== 'Default Title') {
      lines.push(`<span class="cart-drawer__favorite-option">${escapeHtml(item.variant_title)}</span>`);
    }

    if (!lines.length) return '';
    return `<div class="cart-drawer__favorite-options">${lines.join('')}</div>`;
  }

  // ─── Product data ─────────────────────────────────────────

  /**
   * Fetches (and caches) a product's JSON. Returns null on failure.
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  async #fetchProduct(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle) ?? null;

    const root = `${window.Shopify?.routes?.root ?? '/'}`.replace(/\/$/, '');
    try {
      const response = await fetch(`${root}/products/${handle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      this.#productCache.set(handle, product);
      return product;
    } catch {
      this.#productCache.set(handle, null);
      return null;
    }
  }

  /**
   * @param {object|null} product
   * @param {number|null|undefined} variantId
   * @returns {object|null}
   */
  #resolveVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    if (variantId) {
      const match = product.variants.find((variant) => variant.id === variantId);
      if (match) return match;
    }
    return product.variants.find((variant) => variant.available) ?? product.variants[0];
  }

  // ─── Actions ──────────────────────────────────────────────

  /**
   * Adds the variant to the cart and refreshes the drawer via the theme's
   * cart-update → morph pipeline (CartAddEvent carrying the updated sections).
   * @param {object|null} variant
   * @param {HTMLElement} card
   */
  async #addToCart(variant, card) {
    if (!variant?.id) return;

    card.setAttribute('aria-busy', 'true');
    const addButton = card.querySelector('[data-action="add"]');
    if (addButton instanceof HTMLElement) addButton.dataset.state = 'loading';

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections: this.#sectionId,
            sections_url: window.location.pathname,
          }),
          signal: this.#abortController.signal,
        })
      );

      const data = await response.json();

      if (!response.ok || data?.status) {
        card.removeAttribute('aria-busy');
        return;
      }

      document.dispatchEvent(
        // Contrat du product-form : la réponse de /cart/add.js n'a PAS de
        // item_count → le compteur header était remis à zéro (ticket 12796152000).
        // `itemCount` = quantité ajoutée, additionnée par cart-icon grâce à la
        // source form. Le drawer étant déjà ouvert, l'auto-open est un no-op.
        new CartAddEvent(data, this.id || 'cart-drawer-favorites', {
          source: 'product-form-component',
          sections: data.sections,
          itemCount: 1,
        })
      );
    } catch {
      // aborted or network error — leave the card as-is
    } finally {
      card.removeAttribute('aria-busy');
      if (addButton instanceof HTMLElement) delete addButton.dataset.state;
    }
  }

  /**
   * @param {WishlistItem} item
   * @param {HTMLElement} card
   */
  async #removeFavorite(item, card) {
    const wishlist = window.Maestrooo?.wishlist;
    if (!wishlist?.removeProduct) return;

    // Même état de chargement que l'ajout : grise la carte le temps de la requête.
    card.setAttribute('aria-busy', 'true');

    try {
      await wishlist.removeProduct(item.handle, item.variant_id ?? null);
      // The app dispatches `ooo-wishlist:product-removed`, which re-renders us
      // (this card is discarded). On failure we restore the card below.
    } catch {
      card.removeAttribute('aria-busy');
    }
  }

  // ─── Helpers ──────────────────────────────────────────────

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }
}

const CLOSE_SVG =
  '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

/**
 * Appends a width to a Shopify CDN image URL (no-op for non-CDN/empty urls).
 * @param {string} url
 * @param {number} width
 */
function sizedImage(url, width) {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('cart-drawer-favorites')) {
  customElements.define('cart-drawer-favorites', CartDrawerFavorites);
}
